import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, Filter, Grid3X3, List, ExternalLink, Send, Shield,
  MapPin, AlertTriangle, ChevronLeft, ChevronRight, Eye, Lock,
  X, SlidersHorizontal
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Source badge config
const SOURCE_CONFIG = {
  fbi_top_ten: { label: "FBI TOP 10", color: "bg-red-600 text-white", border: "border-red-500/50", glow: "shadow-red-500/20" },
  texas_dps: { label: "TEXAS DPS", color: "bg-amber-600 text-white", border: "border-amber-500/50", glow: "shadow-amber-500/20" },
  us_marshals: { label: "US MARSHALS", color: "bg-blue-600 text-white", border: "border-blue-500/50", glow: "shadow-blue-500/20" },
  lapd: { label: "LAPD", color: "bg-sky-700 text-white", border: "border-sky-500/50", glow: "shadow-sky-500/20" },
  ny_doccs: { label: "NY DOCCS", color: "bg-orange-600 text-white", border: "border-orange-500/50", glow: "shadow-orange-500/20" },
};

const getSourceConfig = (source) => SOURCE_CONFIG[source] || { label: source?.toUpperCase(), color: "bg-slate-600 text-white", border: "border-slate-500/50", glow: "" };

// Fugitive card component
const FugitiveCard = ({ fugitive, isGrid, user, navigate }) => {
  const srcCfg = getSourceConfig(fugitive.source);
  const hasPhoto = fugitive.photo_url && fugitive.photo_url !== "N/A";
  const isLocked = !user;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      className={`card-fugitive group relative ${srcCfg.border} ${isGrid ? "" : "flex gap-0"}`}
      data-testid={`fugitive-card-${fugitive.name?.replace(/\s+/g, '-').toLowerCase()}`}
    >
      {/* Photo */}
      <div className={`relative overflow-hidden bg-slate-800 ${isGrid ? "aspect-[3/4]" : "w-28 h-28 flex-shrink-0"}`}>
        {hasPhoto ? (
          <img
            src={fugitive.photo_url}
            alt={fugitive.name}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
          />
        ) : null}
        <div className={`${hasPhoto ? 'hidden' : 'flex'} w-full h-full items-center justify-center bg-slate-800`}>
          <User className="w-10 h-10 text-slate-600" />
        </div>

        {/* Source badge */}
        <div className={`absolute top-2 left-2 px-2 py-0.5 text-[10px] font-bold tracking-wider ${srcCfg.color} rounded-sm shadow-lg ${srcCfg.glow}`}>
          {srcCfg.label}
        </div>

        {/* Status overlay for captured */}
        {fugitive.description?.status === 'CAPTURED' && (
          <div className="absolute inset-0 bg-green-900/70 flex items-center justify-center">
            <span className="text-green-400 font-heading font-bold text-lg tracking-wider rotate-[-15deg] border-2 border-green-400 px-4 py-1">CAPTURED</span>
          </div>
        )}
      </div>

      {/* Info */}
      <div className={`p-3 flex flex-col ${isGrid ? "" : "flex-1 justify-between"}`}>
        <div>
          <h3 className="font-heading font-bold text-white text-sm leading-tight mb-1 group-hover:text-cyan-400 transition-colors line-clamp-2">
            {fugitive.name}
          </h3>

          {fugitive.state && (
            <div className="flex items-center gap-1 text-slate-500 text-xs mb-1.5">
              <MapPin className="w-3 h-3" />
              <span>{fugitive.last_known_location || fugitive.state}</span>
            </div>
          )}

          {/* Reward - teaser or full */}
          {fugitive.reward_amount && (
            <p className="text-amber-400 text-xs font-mono font-bold mb-1.5 line-clamp-1">
              {isLocked ? "REWARD AVAILABLE" : (typeof fugitive.reward_amount === 'string' ? fugitive.reward_amount.substring(0, 80) : `$${fugitive.reward_amount.toLocaleString()}`)}
            </p>
          )}

          {/* Caution - blurred for non-auth */}
          {fugitive.caution && (
            <p className={`text-xs text-slate-400 line-clamp-2 ${isLocked ? "blur-[3px] select-none" : ""}`}>
              {fugitive.caution}
            </p>
          )}

          {/* Description details - locked */}
          {isLocked && fugitive.caution && (
            <div className="flex items-center gap-1 mt-1 text-cyan-400 text-[10px] font-mono">
              <Lock className="w-3 h-3" />
              <span>Login for full details</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className={`flex gap-2 mt-2 ${isGrid ? "" : ""}`}>
          {fugitive.details_url && (
            <a
              href={fugitive.details_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-cyan-400 transition-colors font-mono tracking-wider"
              data-testid={`fugitive-source-link-${fugitive.name?.replace(/\s+/g, '-').toLowerCase()}`}
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="w-3 h-3" />
              SOURCE
            </a>
          )}
          <button
            onClick={() => navigate(user ? '/tips' : '/login')}
            className="flex items-center gap-1 text-[10px] text-red-400 hover:text-red-300 transition-colors font-mono tracking-wider ml-auto"
            data-testid={`fugitive-tip-btn-${fugitive.name?.replace(/\s+/g, '-').toLowerCase()}`}
          >
            <Send className="w-3 h-3" />
            SUBMIT TIP
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// Missing User icon import fix
const User = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
  </svg>
);

export default function StateFugitivesPage() {
  const navigate = useNavigate();
  const [fugitives, setFugitives] = useState([]);
  const [sources, setSources] = useState({});
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedSource, setSelectedSource] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [showFilters, setShowFilters] = useState(false);
  const LIMIT = 24;

  // Get user from localStorage
  const [user, setUser] = useState(null);
  useEffect(() => {
    const token = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");
    if (token && storedUser) {
      try { setUser(JSON.parse(storedUser)); } catch { setUser(null); }
    }
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch sources on mount
  useEffect(() => {
    api.get("/state-fugitives/sources")
      .then(res => setSources(res.data.sources || {}))
      .catch(() => {});
  }, []);

  // Fetch fugitives
  const fetchFugitives = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", LIMIT);
      params.set("skip", page * LIMIT);
      if (selectedSource) params.set("source", selectedSource);
      if (selectedState) params.set("state", selectedState);
      if (debouncedSearch) params.set("search", debouncedSearch);

      const res = await api.get(`/state-fugitives?${params.toString()}`);
      setFugitives(res.data.fugitives || []);
      setTotal(res.data.total || 0);
    } catch {
      setFugitives([]);
    } finally {
      setLoading(false);
    }
  }, [page, selectedSource, selectedState, debouncedSearch]);

  useEffect(() => {
    fetchFugitives();
  }, [fetchFugitives]);

  // Reset page when filters change
  useEffect(() => { setPage(0); }, [selectedSource, selectedState, debouncedSearch]);

  const totalPages = Math.ceil(total / LIMIT);

  // Get unique states from fugitives for filter dropdown
  const allStates = [...new Set(Object.values(sources).flatMap(s => s.states?.map(st => st.state) || []))].sort();

  return (
    <div className="min-h-screen bg-slate-950" data-testid="state-fugitives-page">
      {/* Top nav bar */}
      <div className="sticky top-0 z-50 bg-slate-950/95 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            data-testid="back-to-home-btn"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="font-heading font-bold text-lg tracking-tight text-white">WYSSPR</span>
          </button>
          <div className="flex items-center gap-3">
            {user ? (
              <button
                onClick={() => navigate("/dashboard")}
                className="text-xs font-mono text-cyan-400 hover:text-cyan-300 tracking-wider"
                data-testid="go-to-dashboard-btn"
              >
                DASHBOARD
              </button>
            ) : (
              <button
                onClick={() => navigate("/login")}
                className="bg-cyan-500 text-black text-xs font-bold px-4 py-1.5 rounded-sm hover:bg-cyan-400 transition-colors tracking-wider"
                data-testid="login-btn"
              >
                LOGIN
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="relative py-16 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-red-950/20 via-slate-950 to-slate-950" />
        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center justify-center gap-2 mb-4">
              <Shield className="w-5 h-5 text-red-400" />
              <p className="text-red-400 font-mono text-sm tracking-[0.3em]">MULTI-AGENCY DATABASE</p>
            </div>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter uppercase text-white mb-4">
              State & Federal Fugitives
            </h1>
            <p className="text-slate-400 text-base md:text-lg max-w-2xl mx-auto mb-3">
              Real-time data from {Object.keys(sources).length} law enforcement agencies.{" "}
              <span className="text-white font-bold">{total.toLocaleString()}</span> wanted persons tracked across the United States.
            </p>
            {!user && (
              <p className="text-cyan-400 text-xs font-mono tracking-wider">
                <Lock className="w-3 h-3 inline mr-1" />
                Login for full fugitive details, charges, and reward information
              </p>
            )}
          </motion.div>
        </div>
      </section>

      {/* Source badges summary */}
      <div className="max-w-7xl mx-auto px-4 mb-6">
        <div className="flex flex-wrap gap-2 justify-center">
          <button
            onClick={() => setSelectedSource("")}
            className={`px-3 py-1.5 text-xs font-mono tracking-wider rounded-sm border transition-all ${
              !selectedSource ? "bg-cyan-500/20 border-cyan-500/50 text-cyan-400" : "bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-600"
            }`}
            data-testid="filter-all-sources"
          >
            ALL ({total.toLocaleString()})
          </button>
          {Object.entries(sources).map(([key, val]) => {
            const cfg = getSourceConfig(key);
            return (
              <button
                key={key}
                onClick={() => setSelectedSource(selectedSource === key ? "" : key)}
                className={`px-3 py-1.5 text-xs font-mono tracking-wider rounded-sm border transition-all ${
                  selectedSource === key
                    ? `${cfg.color} ${cfg.border}`
                    : `bg-slate-900 border-slate-700 text-slate-400 hover:${cfg.border}`
                }`}
                data-testid={`filter-source-${key}`}
              >
                {cfg.label} ({val.count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Search & Controls */}
      <div className="max-w-7xl mx-auto px-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              placeholder="Search fugitives by name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 bg-slate-900 border border-slate-700 rounded-sm text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-500 transition-colors font-mono"
              data-testid="fugitive-search-input"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Filters toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2.5 border rounded-sm text-sm font-mono tracking-wider transition-all ${
              showFilters || selectedState ? "bg-cyan-500/10 border-cyan-500/50 text-cyan-400" : "bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-600"
            }`}
            data-testid="toggle-filters-btn"
          >
            <SlidersHorizontal className="w-4 h-4" />
            FILTERS
            {selectedState && <span className="w-2 h-2 bg-cyan-400 rounded-full" />}
          </button>

          {/* View mode toggle */}
          <div className="flex border border-slate-700 rounded-sm overflow-hidden">
            <button
              onClick={() => setViewMode("grid")}
              className={`p-2.5 transition-colors ${viewMode === "grid" ? "bg-cyan-500/20 text-cyan-400" : "bg-slate-900 text-slate-500 hover:text-slate-300"}`}
              data-testid="view-grid-btn"
            >
              <Grid3X3 className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`p-2.5 transition-colors ${viewMode === "list" ? "bg-cyan-500/20 text-cyan-400" : "bg-slate-900 text-slate-500 hover:text-slate-300"}`}
              data-testid="view-list-btn"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Expanded filters */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="pt-3 flex flex-wrap gap-3">
                <select
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  className="bg-slate-900 border border-slate-700 rounded-sm px-3 py-2 text-sm text-slate-300 font-mono focus:outline-none focus:border-cyan-500"
                  data-testid="filter-state-select"
                >
                  <option value="">All States</option>
                  {allStates.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                {(selectedSource || selectedState || search) && (
                  <button
                    onClick={() => { setSelectedSource(""); setSelectedState(""); setSearch(""); }}
                    className="text-xs text-red-400 hover:text-red-300 font-mono tracking-wider flex items-center gap-1"
                    data-testid="clear-filters-btn"
                  >
                    <X className="w-3 h-3" /> CLEAR ALL
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Results count */}
      <div className="max-w-7xl mx-auto px-4 mb-4">
        <p className="text-slate-500 text-xs font-mono">
          {loading ? "Searching..." : `${total.toLocaleString()} fugitives found`}
          {(selectedSource || selectedState || debouncedSearch) && " (filtered)"}
        </p>
      </div>

      {/* Fugitive grid / list */}
      <div className="max-w-7xl mx-auto px-4 pb-8">
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="card-fugitive animate-pulse">
                <div className="aspect-[3/4] bg-slate-800" />
                <div className="p-3 space-y-2">
                  <div className="h-3 bg-slate-800 rounded w-3/4" />
                  <div className="h-2 bg-slate-800 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : fugitives.length === 0 ? (
          <div className="text-center py-20">
            <AlertTriangle className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 font-heading text-lg">No fugitives found</p>
            <p className="text-slate-600 text-sm mt-1">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className={
            viewMode === "grid"
              ? "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3"
              : "flex flex-col gap-2"
          }>
            <AnimatePresence mode="popLayout">
              {fugitives.map((f, i) => (
                <FugitiveCard
                  key={`${f.source}-${f.name}-${i}`}
                  fugitive={f}
                  isGrid={viewMode === "grid"}
                  user={user}
                  navigate={navigate}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="max-w-7xl mx-auto px-4 pb-12">
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="flex items-center gap-1 px-4 py-2 border border-slate-700 rounded-sm text-xs font-mono text-slate-400 hover:border-cyan-500 hover:text-cyan-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              data-testid="prev-page-btn"
            >
              <ChevronLeft className="w-3 h-3" /> PREV
            </button>
            <span className="text-xs font-mono text-slate-500">
              PAGE {page + 1} OF {totalPages}
            </span>
            <button
              onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1}
              className="flex items-center gap-1 px-4 py-2 border border-slate-700 rounded-sm text-xs font-mono text-slate-400 hover:border-cyan-500 hover:text-cyan-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              data-testid="next-page-btn"
            >
              NEXT <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {/* CTA banner */}
      {!user && (
        <section className="py-16 px-4 bg-gradient-to-t from-slate-950 via-red-950/10 to-slate-950 border-t border-slate-800">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-white uppercase mb-3">
              Help Bring Them to Justice
            </h2>
            <p className="text-slate-400 mb-6">
              Create a free account to access full fugitive profiles, submit tips, and earn rewards.
            </p>
            <button
              onClick={() => navigate("/login")}
              className="btn-primary"
              data-testid="cta-signup-btn"
            >
              Sign Up Free
            </button>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-6 px-4 border-t border-slate-800">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-xs text-slate-600 font-mono">
          <span>&copy; 2026 Wysspr Intelligence</span>
          <button onClick={() => navigate("/")} className="hover:text-slate-400 transition-colors">
            Back to Home
          </button>
        </div>
      </footer>
    </div>
  );
}
